import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CloseIcon } from '@yme/assets/icons';
import { INPUT_STATE_ERROR } from '@yme/atoms/input';
import { SpinnerLoader } from '@yme/atoms/spinner-loader';
import { ICON_SIZE, SVGIcon } from '@yme/atoms/svg-icon';
import { noop } from '@yme/utils/common';
import { EVENT_NAME_MAP } from '@yme/utils/constants';
import { DropDownResults } from './DropDownResults';
import { AutocompleteRequestStyled, ControlPanelStyled, InputStyled } from './styled';
export const AutocompleteRequestInputId = 'autocompleteRequestInput';
export function AutocompleteRequest({ label, value, placeholder, minCharacters = 2, isLoading = false, suggestionList, infoText, isItemWithImage = false, noResultDescription, noResultTitle, useForMobile = false, hasError, inputImage, isRightToLeft, icon, ControlButtons, dropdownMaxHeightValue, resultsListRangeOfMaxVisibleItems, onChange, onClear, onSelect, onBlur, onScroll, onKeyDownHandler, isFocused, }) {
    const inputWrapperRef = useRef(null);
    const inputRef = useRef(null);
    const [isExpandedDropDown, setIsExpandedDropDown] = useState(false);
    const [isEditMode, setIsEditMode] = useState(true);
    const [initValue] = useState(value);
    const Controls = useMemo(() => Boolean(ControlButtons) && (React.createElement(ControlPanelStyled, { isRightToLeft: isRightToLeft },
        Boolean(value.length) && (React.createElement(SVGIcon, { type: CloseIcon, tabIndex: 0, size: ICON_SIZE.s, onClick: onClear })),
        ControlButtons)), [ControlButtons, onClear, value.length, isRightToLeft]);
    const onChangeHandler = useCallback((currentValue) => (isEditMode ? onChange(currentValue) : null), [isEditMode, onChange]);
    const onFocusHandler = useCallback(() => {
        setIsEditMode(true);
    }, []);
    const onBlurHandler = useCallback((event) => {
        if (!inputWrapperRef?.current?.contains(event.relatedTarget)) {
            setIsEditMode(false);
            onBlur?.();
        }
    }, [onBlur]);
    const onSelectHandler = useCallback((selectedItem) => () => {
        setIsEditMode(false);
        setIsExpandedDropDown(false);
        onSelect(selectedItem);
    }, [onSelect]);
    // eslint-disable-next-line consistent-return
    useEffect(() => {
        if (isExpandedDropDown) {
            const handle = (event) => {
                if (!inputWrapperRef?.current?.contains(event.target) &&
                    value.length > 0) {
                    setIsExpandedDropDown(false);
                    onClear();
                }
            };
            document.addEventListener(EVENT_NAME_MAP.click, handle);
            return () => {
                document.removeEventListener(EVENT_NAME_MAP.click, handle);
            };
        }
    }, [setIsExpandedDropDown, inputWrapperRef, value.length, onClear, isExpandedDropDown]);
    useEffect(() => {
        if (isEditMode && value.length >= minCharacters) {
            if (initValue !== value) {
                setIsExpandedDropDown(true);
            }
        }
        if (value.length < minCharacters) {
            setIsExpandedDropDown(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [suggestionList.length, value, initValue]);
    return (React.createElement(AutocompleteRequestStyled, { ref: inputWrapperRef },
        React.createElement(SpinnerLoader, { isLoading: isLoading },
            React.createElement(InputStyled, { id: AutocompleteRequestInputId, name: "autocompleteRequestInput", placeholder: placeholder, value: value, label: label, leftIcon: inputImage || icon, isInputImage: Boolean(inputImage), state: hasError ? INPUT_STATE_ERROR : undefined, infoText: infoText || '', multipleIconsBlock: Controls, blurHandler: onBlurHandler, focusHandler: onFocusHandler, changeHandler: onChangeHandler, onKeyDownHandler: onKeyDownHandler, isRequired: true, isDisabled: false, isRightToLeft: isRightToLeft, isFocused: isFocused, ref: inputRef }),
            React.createElement(DropDownResults, { isRightToLeft: isRightToLeft, activeItem: "", rangeOfMaxVisibleItems: resultsListRangeOfMaxVisibleItems, isExpandedDropDown: isExpandedDropDown, useForMobile: useForMobile, noResultDescription: noResultDescription || '', noResultTitle: noResultTitle || '', maxHeightValue: dropdownMaxHeightValue, isItemWithImage: isItemWithImage, suggestionList: suggestionList, currIndex: 0, inputValue: value, isEditMode: isEditMode, searchedValue: "", handleSelect: onSelectHandler, selectedValueByKeyboard: 0, handleEnter: () => noop, onScroll: onScroll }))));
}
AutocompleteRequest.displayName = 'AutocompleteRequest';
